import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BRAND } from "../constants/brand";
import { useAuth } from "../context/AuthContext";
import { GoogleG } from "../components/marketing/SignupPopup";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const user = await signup(form);
      toast.success("Account created!");
      nav(user.role === "vendor" ? "/vendor/dashboard" : "/customer/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally { setBusy(false); }
  };

  const goGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-neutral-950 text-white grain md:flex md:flex-col md:justify-between md:p-10">
        <Link to="/" className="flex items-center gap-2">
          <img src={BRAND.logo} alt="EarlyPrint" className="h-10 w-10 rounded-full" />
          <span className="font-display text-xl font-bold">Early<span className="text-lime-400">Print</span></span>
        </Link>
        <div>
          <h1 className="font-display text-5xl font-black leading-tight tracking-tight">Start printing <span className="text-lime-400">in minutes.</span></h1>
          <p className="mt-4 max-w-md text-sm text-neutral-300">Join thousands of customers and hundreds of verified print shops across {BRAND.location}.</p>
        </div>
        <div className="text-xs text-neutral-500">© {new Date().getFullYear()} {BRAND.legalName}</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <h2 className="font-display text-3xl font-bold" data-testid="signup-title">Create your account</h2>
          <p className="mt-1 text-sm text-neutral-600">One tap with Google, or use your email below.</p>

          <button onClick={goGoogle} className="mt-6 flex w-full items-center justify-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-3 text-sm font-semibold hover:bg-neutral-50" data-testid="signup-google-btn">
            <GoogleG /> Continue with Google
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-neutral-400">
            <div className="h-px flex-1 bg-neutral-200" /> OR <div className="h-px flex-1 bg-neutral-200" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className={`flex cursor-pointer items-center justify-center rounded-md border p-3 text-sm font-semibold ${form.role === "customer" ? "border-neutral-900 bg-lime-100" : "border-neutral-300 hover:border-neutral-900"}`}>
                <input type="radio" name="role" className="hidden" checked={form.role === "customer"} onChange={() => setForm((f) => ({ ...f, role: "customer" }))} data-testid="signup-role-customer" />
                I want to order
              </label>
              <label className={`flex cursor-pointer items-center justify-center rounded-md border p-3 text-sm font-semibold ${form.role === "vendor" ? "border-neutral-900 bg-lime-100" : "border-neutral-300 hover:border-neutral-900"}`}>
                <input type="radio" name="role" className="hidden" checked={form.role === "vendor"} onChange={() => setForm((f) => ({ ...f, role: "vendor" }))} data-testid="signup-role-vendor" />
                I'm a print shop
              </label>
            </div>
            <input placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-neutral-300 px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid="signup-name" />
            <input type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-neutral-300 px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid="signup-email" />
            <input type="password" placeholder="Password (min 6 chars)" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-md border border-neutral-300 px-3 py-3 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" data-testid="signup-password" />
            <button type="submit" disabled={busy} className="btn-offset w-full rounded-md bg-neutral-950 px-4 py-3 text-sm font-bold text-white hover:bg-neutral-800 disabled:opacity-60" data-testid="signup-submit">{busy ? "Creating…" : "Create Account"}</button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-600">Already have an account? <Link to="/login" className="font-semibold text-neutral-900 hover:text-lime-500" data-testid="signup-to-login">Login</Link></p>
        </div>
      </div>
    </div>
  );
}
